import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { ArrowUpRight, Inbox, Plus, RotateCcw, Search, X } from 'lucide-react';
import type { TeamRef } from '@central/contracts';
import { Button } from '@/components/Button';
import { Card, CardBody } from '@/components/Card';
import { cn } from '@/lib/cn';
import {
  BarraDeProgresso,
  EtiquetaDePrioridade,
  EtiquetaDeStatus,
  EtiquetaDeTipo,
} from '@/features/tickets/badges';
import { formatarDataHora, formatarDistancia, mensagemDeErro } from '@/lib/format';
import { chavesDeApoio, obterOpcoesDeFormulario } from '@/features/tickets/lookups.api';
import { LinkDeChamado, useBuscaDaLista, useIrParaChamado } from '@/features/tickets/navigation';
import { NewTicketDialog } from '@/features/tickets/NewTicketDialog';
import {
  NomeDePessoa,
  useDicionarioDePessoas,
  type DicionarioDePessoas,
} from '@/features/tickets/pessoas';
import {
  chaves,
  listarChamados,
  listarComentarios,
  type FiltrosDeChamado,
} from '@/features/tickets/tickets.api';
import {
  CATEGORIAS_DE_STATUS,
  ROTULO_DE_STATUS,
  type CategoriaDeStatus,
  type Chamado,
  type Comentario,
  type TipoDeChamado,
} from '@/features/tickets/types';
import {
  listaDeTipos,
  rotuloDaPrioridade,
  rotuloDeTipo,
  useOpcoesDeFormulario,
} from '@/features/tickets/opcoes';

const POR_PAGINA = 25;

/**
 * Caixa de entrada dos chamados.
 *
 * A paginação é por cursor, e não por página numerada, porque a ordenação é
 * pelo `id` (UUIDv7, crescente no tempo): chamado aberto durante a navegação
 * entra no topo e empurraria tudo para baixo, fazendo a "página 2" repetir
 * linhas que a 1 já mostrou.
 */
export function TicketListPage() {
  const irParaChamado = useIrParaChamado();

  const [tipos, setTipos] = useState<TipoDeChamado[]>([]);
  const [categorias, setCategorias] = useState<CategoriaDeStatus[]>([]);
  const [somenteRaizes, setSomenteRaizes] = useState(true);
  const [somenteAbertos, setSomenteAbertos] = useState(true);
  const [abrindo, setAbrindo] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { termo: busca, definir: definirBusca } = useBuscaDaLista();
  const [textoDigitado, setTextoDigitado] = useState(busca);

  // A busca global da casca troca a URL com esta tela já montada; sem isto o
  // campo continuaria mostrando o termo anterior enquanto a lista filtra pelo
  // novo.
  useEffect(() => setTextoDigitado(busca), [busca]);

  const filtros = useMemo<FiltrosDeChamado>(
    () => ({
      limit: POR_PAGINA,
      rootsOnly: somenteRaizes,
      openOnly: somenteAbertos,
      ...(tipos.length ? { type: tipos } : {}),
      ...(categorias.length ? { status: categorias } : {}),
      ...(busca ? { q: busca } : {}),
    }),
    [tipos, categorias, somenteRaizes, somenteAbertos, busca],
  );

  const consulta = useInfiniteQuery({
    queryKey: chaves.lista(filtros),
    queryFn: ({ pageParam }) => listarChamados(filtros, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (ultima) => ultima.nextCursor ?? undefined,
  });

  const chamados: Chamado[] = consulta.data?.pages.flatMap((pagina) => pagina.items) ?? [];
  const temFiltro = tipos.length > 0 || categorias.length > 0 || busca !== '';
  const selectedChamado = selectedId ? (chamados.find((c) => c.id === selectedId) ?? null) : null;

  // Resolve assignees e solicitantes de uma vez — sem esta lista ampliada, o
  // painel de prévia não conseguiria exibir o nome do solicitante.
  const pessoas = useDicionarioDePessoas([
    ...chamados.map((c) => c.assigneeId),
    ...chamados.map((c) => c.requesterId),
  ]);

  // Equipes do formulário — já buscadas pelo NewTicketDialog, logo estão em
  // cache. Usadas para resolver teamId → nome na coluna "Fila".
  const opcoesQ = useQuery({
    queryKey: chavesDeApoio.formulario,
    queryFn: obterOpcoesDeFormulario,
    staleTime: 5 * 60 * 1000,
  });
  const equipePorId = useMemo(() => {
    const mapa = new Map<string, TeamRef>();
    opcoesQ.data?.teams.forEach((t) => mapa.set(t.id, t));
    return mapa;
  }, [opcoesQ.data]);

  function buscar(evento: FormEvent) {
    evento.preventDefault();
    definirBusca(textoDigitado.trim());
  }

  function limparFiltros() {
    setTipos([]);
    setCategorias([]);
    setTextoDigitado('');
    definirBusca('');
    setSomenteRaizes(true);
    setSomenteAbertos(true);
  }

  const panelOpen = selectedChamado !== null;

  return (
    <div className={cn('mx-auto space-y-3', panelOpen ? 'max-w-[1600px]' : 'max-w-[1400px]')}>
      <header className="flex items-center justify-between gap-4 border-b border-border pb-3">
        <h1 className="text-base font-semibold">Chamados</h1>
        <Button size="sm" onClick={() => setAbrindo(true)} icon={<Plus className="h-4 w-4" aria-hidden />}>
          Abrir chamado
        </Button>
      </header>

      <Card>
        <CardBody className="space-y-4">
          <form onSubmit={buscar} className="flex flex-wrap items-center gap-2">
            <div className="relative min-w-[16rem] flex-1">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                aria-hidden
              />
              <input
                value={textoDigitado}
                onChange={(evento) => setTextoDigitado(evento.target.value)}
                placeholder="Assunto ou identificador (I2603-0001)"
                aria-label="Buscar chamados"
                className="w-full rounded-lg border border-input/80 bg-muted/40 py-2 pl-9 pr-3 text-sm transition-colors placeholder:text-muted-foreground hover:bg-muted focus-visible:border-ring/40 focus-visible:bg-background focus-visible:outline-none focus-visible:shadow-glow"
              />
            </div>
            <Button type="submit" variant="secondary">
              Buscar
            </Button>
            {temFiltro ? (
              <Button
                type="button"
                variant="ghost"
                onClick={limparFiltros}
                icon={<X className="h-4 w-4" aria-hidden />}
              >
                Limpar
              </Button>
            ) : null}
          </form>

          <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
            <GrupoDeFiltro rotulo="Tipo">
              {listaDeTipos(opcoesQ.data).map((t) => (
                <Ficha
                  key={t.type}
                  ativo={tipos.includes(t.type)}
                  aoAlternar={() => setTipos(alternar(tipos, t.type))}
                >
                  {t.label}
                </Ficha>
              ))}
            </GrupoDeFiltro>

            <GrupoDeFiltro rotulo="Situação">
              {CATEGORIAS_DE_STATUS.map((categoria) => (
                <Ficha
                  key={categoria}
                  ativo={categorias.includes(categoria)}
                  desabilitado={somenteAbertos}
                  titulo={somenteAbertos ? 'Desligue "Só abertos" para filtrar por situação' : undefined}
                  aoAlternar={() => setCategorias(alternar(categorias, categoria))}
                >
                  {ROTULO_DE_STATUS[categoria]}
                </Ficha>
              ))}
            </GrupoDeFiltro>

            <div className="flex items-center gap-1.5">
              <Ficha ativo={somenteRaizes} aoAlternar={() => setSomenteRaizes(!somenteRaizes)}>
                Só raízes
              </Ficha>
              <Ficha ativo={somenteAbertos} aoAlternar={() => setSomenteAbertos(!somenteAbertos)}>
                Só abertos
              </Ficha>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Lista + painel lateral em flex — o painel empurra a tabela para a esquerda */}
      <div className="flex items-start gap-4">
        <div className="min-w-0 flex-1">
          <Card>
            <CardBody className="p-0">
              {consulta.isError ? (
                <EstadoDeErro
                  mensagem={
                    mensagemDeErro(consulta.error) ?? 'Não foi possível carregar os chamados.'
                  }
                  aoTentarDeNovo={() => void consulta.refetch()}
                />
              ) : consulta.isPending ? (
                <Esqueleto />
              ) : chamados.length === 0 ? (
                <EstadoVazio
                  comFiltro={temFiltro}
                  aoLimpar={limparFiltros}
                  aoAbrir={() => setAbrindo(true)}
                />
              ) : (
                <div className="animate-fade-in overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border bg-muted/30 text-left text-xs uppercase tracking-wider text-muted-foreground">
                        <Cabecalho>Identificador</Cabecalho>
                        <Cabecalho>Assunto</Cabecalho>
                        <Cabecalho>Fila</Cabecalho>
                        <Cabecalho>Prioridade</Cabecalho>
                        <Cabecalho>Situação</Cabecalho>
                        <Cabecalho>Responsável</Cabecalho>
                        <Cabecalho>Vencimento</Cabecalho>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {chamados.map((chamado) => (
                        <Linha
                          key={chamado.id}
                          chamado={chamado}
                          pessoas={pessoas}
                          equipePorId={equipePorId}
                          opcoes={opcoesQ.data}
                          selecionado={chamado.id === selectedId}
                          aoSelecionar={() =>
                            setSelectedId((prev) => (prev === chamado.id ? null : chamado.id))
                          }
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardBody>

            {chamados.length > 0 ? (
              <div className="flex items-center justify-between gap-4 border-t border-border px-5 py-3">
                <span className="text-xs text-muted-foreground">
                  {chamados.length} {chamados.length === 1 ? 'chamado' : 'chamados'} carregados
                </span>
                {consulta.hasNextPage ? (
                  <Button
                    variant="secondary"
                    size="sm"
                    loading={consulta.isFetchingNextPage}
                    onClick={() => void consulta.fetchNextPage()}
                  >
                    Carregar mais
                  </Button>
                ) : (
                  <span className="text-xs text-muted-foreground">Fim da lista</span>
                )}
              </div>
            ) : null}
          </Card>
        </div>

        {selectedChamado ? (
          <PainelDePrevia
            chamado={selectedChamado}
            pessoas={pessoas}
            equipePorId={equipePorId}
            aoFechar={() => setSelectedId(null)}
          />
        ) : null}
      </div>

      <NewTicketDialog
        aberto={abrindo}
        aoFechar={() => setAbrindo(false)}
        aoCriar={(chamado) => irParaChamado(chamado.id)}
      />
    </div>
  );
}

// ── painel de prévia ───────────────────────────────────────────────────────

function PainelDePrevia({
  chamado,
  pessoas,
  equipePorId,
  aoFechar,
}: {
  chamado: Chamado;
  pessoas: DicionarioDePessoas;
  equipePorId: Map<string, TeamRef>;
  aoFechar: () => void;
}) {
  const opcoesPrevia = useOpcoesDeFormulario();
  const comentariosQ = useQuery({
    queryKey: chaves.comentarios(chamado.id),
    queryFn: () => listarComentarios(chamado.id),
  });

  const comentarios = comentariosQ.data ?? [];
  const autores = useDicionarioDePessoas(comentarios.map((c) => c.authorId));

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') aoFechar();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [aoFechar]);

  return (
    <aside className="w-[440px] shrink-0 flex flex-col overflow-hidden rounded-xl border border-border bg-card sticky top-4 max-h-[calc(100vh-6rem)]">
      {/* Número + botões */}
      <div className="flex items-center justify-between gap-2 border-b border-border px-4 py-3">
        <div className="flex min-w-0 items-center gap-2">
          <code className="shrink-0 font-mono text-xs font-semibold text-primary">
            {chamado.number}
          </code>
          <EtiquetaDeTipo tipo={chamado.type} label={rotuloDeTipo(opcoesPrevia.data, chamado.type)} />
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <LinkDeChamado
            ticketId={chamado.id}
            className="flex items-center gap-1 rounded-md px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:shadow-glow"
          >
            Abrir
            <ArrowUpRight className="h-3 w-3" aria-hidden />
          </LinkDeChamado>
          <button
            type="button"
            onClick={aoFechar}
            aria-label="Fechar prévia"
            className="grid h-7 w-7 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:shadow-glow"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Assunto + situação + prioridade */}
      <div className="border-b border-border px-4 py-3">
        <p className="font-medium leading-snug">{chamado.subject}</p>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <EtiquetaDeStatus status={chamado.statusCategory} />
          <EtiquetaDePrioridade
            prioridade={chamado.priority}
            label={rotuloDaPrioridade(opcoesPrevia.data, chamado.priority)}
            sobrescrita={chamado.priorityOverridden}
          />
        </div>
      </div>

      {/* Metadados */}
      <div className="grid grid-cols-2 gap-x-4 gap-y-2 border-b border-border px-4 py-3">
        <MetaItem rotulo="Fila / Equipe">
          <FilaDaCelula
            teamId={chamado.teamId}
            tier={chamado.supportTier}
            equipePorId={equipePorId}
            compact
            precisaTriagem={chamado.teamId === null && chamado.statusCategory === 'NEW'}
          />
        </MetaItem>
        <MetaItem rotulo="Responsável">
          <NomeDePessoa id={chamado.assigneeId} pessoas={pessoas} semId="Não atribuído" />
        </MetaItem>
        <MetaItem rotulo="Solicitante">
          <NomeDePessoa id={chamado.requesterId} pessoas={pessoas} semId="—" />
        </MetaItem>
        <MetaItem rotulo="Vencimento">
          <Vencimento iso={chamado.dueAt} encerrado={chamado.closedAt !== null} />
        </MetaItem>
        <MetaItem rotulo="Aberto">
          <span className="text-xs" title={formatarDataHora(chamado.openedAt)}>
            {formatarDistancia(chamado.openedAt)}
          </span>
        </MetaItem>
      </div>

      {/* Descrição */}
      {chamado.descriptionHtml || chamado.descriptionText ? (
        <div className="border-b border-border px-4 py-3">
          <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            Descrição
          </p>
          <div
            className="max-h-28 overflow-y-auto text-xs leading-relaxed [&_p:last-child]:mb-0 [&_p]:mt-0"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: chamado.descriptionHtml ?? chamado.descriptionText ?? '',
            }}
          />
        </div>
      ) : null}

      {/* Ações (comentários) */}
      <div className="flex flex-1 flex-col overflow-hidden">
        <p className="border-b border-border px-4 py-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          Ações recentes{comentarios.length ? ` (${comentarios.length})` : ''}
        </p>
        <div className="flex-1 space-y-3 overflow-y-auto px-4 py-3">
          {comentariosQ.isPending ? (
            <p className="text-xs text-muted-foreground">Carregando…</p>
          ) : comentarios.length === 0 ? (
            <p className="text-xs text-muted-foreground">Nenhuma ação registrada.</p>
          ) : (
            [...comentarios]
              .reverse()
              .map((comentario) => (
                <ComentarioDePrevia key={comentario.id} comentario={comentario} autores={autores} />
              ))
          )}
        </div>
      </div>
    </aside>
  );
}

function MetaItem({ rotulo, children }: { rotulo: string; children: ReactNode }) {
  return (
    <div>
      <span className="block text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {rotulo}
      </span>
      {children}
    </div>
  );
}

function ComentarioDePrevia({
  comentario,
  autores,
}: {
  comentario: Comentario;
  autores: DicionarioDePessoas;
}) {
  const isSystem = comentario.visibility === 'SYSTEM' || comentario.authorId === null;

  if (isSystem) {
    return (
      <div className="rounded-md bg-muted/50 px-3 py-2">
        <div
          className="text-xs leading-relaxed text-muted-foreground [&_p:last-child]:mb-0 [&_p]:mt-0"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: comentario.bodyHtml }}
        />
        <span className="mt-1 block text-[10px] text-muted-foreground/60">
          {formatarDistancia(comentario.createdAt)}
        </span>
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-xs font-medium">
          <NomeDePessoa id={comentario.authorId} pessoas={autores} semId="Sistema" />
        </span>
        <span className="shrink-0 text-[10px] text-muted-foreground">
          {formatarDistancia(comentario.createdAt)}
        </span>
      </div>
      {comentario.visibility === 'INTERNAL' ? (
        <span className="inline-block rounded bg-warning/10 px-1 py-0.5 text-[10px] font-medium text-warning">
          interno
        </span>
      ) : null}
      <div
        className="text-xs leading-relaxed [&_p:last-child]:mb-0 [&_p]:mt-0"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: comentario.bodyHtml }}
      />
    </div>
  );
}

// ── linha e células ────────────────────────────────────────────────────────

function Linha({
  chamado,
  pessoas,
  equipePorId,
  opcoes,
  selecionado,
  aoSelecionar,
}: {
  chamado: Chamado;
  pessoas: DicionarioDePessoas;
  equipePorId: Map<string, TeamRef>;
  opcoes: import('@central/contracts').TicketFormOptions | undefined;
  selecionado: boolean;
  aoSelecionar: () => void;
}) {
  return (
    <tr
      className={cn(
        'cursor-pointer transition-colors hover:bg-accent/50',
        selecionado && 'bg-primary/5 hover:bg-primary/8',
      )}
      onClick={(e) => {
        // Deixa links (número mono) navegar sem abrir o painel
        if ((e.target as HTMLElement).closest('a')) return;
        aoSelecionar();
      }}
    >
      <Celula>
        <LinkDeChamado
          ticketId={chamado.id}
          className="font-mono text-xs font-semibold text-primary hover:underline"
        >
          {chamado.number}
        </LinkDeChamado>
      </Celula>

      <Celula className="max-w-xs">
        <span className="block truncate">{chamado.subject}</span>
        <div className="mt-1 flex items-center gap-2">
          <EtiquetaDeTipo tipo={chamado.type} label={rotuloDeTipo(opcoes, chamado.type)} />
          {chamado.childCount > 0 ? (
            <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
              <BarraDeProgresso percentual={chamado.progressPct} className="w-16" />
              {chamado.childCount - chamado.openChildCount}/{chamado.childCount} subchamados
            </span>
          ) : null}
        </div>
      </Celula>

      <Celula>
        <FilaDaCelula
          teamId={chamado.teamId}
          tier={chamado.supportTier}
          equipePorId={equipePorId}
          precisaTriagem={chamado.teamId === null && chamado.statusCategory === 'NEW'}
        />
      </Celula>

      <Celula>
        <EtiquetaDePrioridade
          prioridade={chamado.priority}
          label={rotuloDaPrioridade(opcoes, chamado.priority)}
          sobrescrita={chamado.priorityOverridden}
        />
      </Celula>

      <Celula>
        <EtiquetaDeStatus status={chamado.statusCategory} />
      </Celula>

      <Celula className="text-muted-foreground">
        <NomeDePessoa
          id={chamado.assigneeId}
          pessoas={pessoas}
          semId="Não atribuído"
          className="block max-w-[10rem] truncate"
        />
      </Celula>

      <Celula>
        <Vencimento iso={chamado.dueAt} encerrado={chamado.closedAt !== null} />
      </Celula>
    </tr>
  );
}

/**
 * Célula da coluna "Fila".
 *
 * N1 aparece discreto (cinza) — é o estado normal. N2+ aparece em âmbar
 * com fundo colorido para chamar atenção: significa que o chamado foi
 * escalado e sair da fila de primeiro nível. Chamados sem equipe e em
 * triagem ganham um pill âmbar "Triagem" em vez do traço vazio.
 */
function FilaDaCelula({
  teamId,
  tier,
  equipePorId,
  compact = false,
  precisaTriagem = false,
}: {
  teamId: string | null;
  tier: number | null;
  equipePorId: Map<string, TeamRef>;
  compact?: boolean;
  precisaTriagem?: boolean;
}) {
  const equipe = teamId ? equipePorId.get(teamId) : null;
  const escalado = (tier ?? 1) > 1;

  if (!equipe && tier == null) {
    if (precisaTriagem) {
      return (
        <span className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide bg-warning/15 text-warning ring-1 ring-inset ring-warning/30">
          Triagem
        </span>
      );
    }
    return <span className="text-xs text-muted-foreground">—</span>;
  }

  return (
    <div className={cn('flex flex-col gap-0.5', compact && 'flex-row items-center gap-1.5')}>
      {tier !== null ? (
        <span
          className={cn(
            'self-start rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide',
            escalado
              ? 'bg-warning/15 text-warning ring-1 ring-inset ring-warning/30'
              : 'bg-muted text-muted-foreground ring-1 ring-inset ring-border',
          )}
          title={escalado ? `Escalonado para N${tier}` : 'Primeiro nível (N1)'}
        >
          N{tier}
        </span>
      ) : null}
      {equipe ? (
        <span className={cn('text-xs', escalado ? 'font-medium text-warning' : 'text-foreground')}>
          {equipe.name}
        </span>
      ) : null}
    </div>
  );
}

function Vencimento({ iso, encerrado }: { iso: string | null; encerrado: boolean }) {
  if (!iso) return <span className="text-muted-foreground">—</span>;

  const restante = new Date(iso).getTime() - Date.now();
  const tom = encerrado
    ? 'text-muted-foreground'
    : restante < 0
      ? 'text-destructive font-medium'
      : restante < 4 * 60 * 60 * 1000
        ? 'text-warning font-medium'
        : 'text-muted-foreground';

  return (
    <span className={cn('whitespace-nowrap text-xs', tom)} title={formatarDataHora(iso)}>
      {formatarDistancia(iso)}
    </span>
  );
}

function Cabecalho({ children }: { children?: ReactNode }) {
  return <th className="px-4 py-1.5 font-medium">{children}</th>;
}

function Celula({ className, children }: { className?: string; children: ReactNode }) {
  return <td className={cn('px-4 py-1.5 align-middle', className)}>{children}</td>;
}

// ── filtros ────────────────────────────────────────────────────────────────

function GrupoDeFiltro({ rotulo, children }: { rotulo: string; children: ReactNode }) {
  return (
    <fieldset className="flex flex-wrap items-center gap-1.5">
      <legend className="sr-only">{rotulo}</legend>
      <span className="mr-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {rotulo}
      </span>
      {children}
    </fieldset>
  );
}

function Ficha({
  ativo,
  desabilitado = false,
  titulo,
  aoAlternar,
  children,
}: {
  ativo: boolean;
  desabilitado?: boolean;
  titulo?: string;
  aoAlternar: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={aoAlternar}
      disabled={desabilitado}
      title={titulo}
      aria-pressed={ativo}
      className={cn(
        'rounded-full border px-2.5 py-1 text-xs font-medium transition-colors',
        'disabled:cursor-not-allowed disabled:opacity-40',
        ativo
          ? 'border-primary bg-primary/10 text-primary'
          : 'border-border text-muted-foreground hover:bg-accent hover:text-foreground',
      )}
    >
      {children}
    </button>
  );
}


function alternar<T>(lista: T[], valor: T): T[] {
  return lista.includes(valor) ? lista.filter((item) => item !== valor) : [...lista, valor];
}

// ── estados ────────────────────────────────────────────────────────────────

function Esqueleto() {
  return (
    <div className="space-y-3 px-5 py-5" aria-busy>
      <span className="sr-only">Carregando chamados</span>
      {[0, 1, 2, 3, 4].map((linha) => (
        <div key={linha} className="relative overflow-hidden rounded-md bg-muted/60 py-4">
          <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-background/50 to-transparent" />
        </div>
      ))}
    </div>
  );
}

function EstadoVazio({
  comFiltro,
  aoLimpar,
  aoAbrir,
}: {
  comFiltro: boolean;
  aoLimpar: () => void;
  aoAbrir: () => void;
}) {
  return (
    <div className="flex flex-col items-center gap-3 px-5 py-16 text-center">
      <div className="grid h-12 w-12 place-items-center rounded-full bg-muted">
        <Inbox className="h-5 w-5 text-muted-foreground" aria-hidden />
      </div>
      <div>
        <p className="text-sm font-medium">
          {comFiltro ? 'Nenhum chamado com esses filtros' : 'Nenhum chamado por aqui'}
        </p>
        <p className="mt-1 text-sm text-muted-foreground">
          {comFiltro
            ? 'Tente ampliar a busca ou desligar os filtros.'
            : 'Quando alguém abrir um chamado, ele aparece nesta lista.'}
        </p>
      </div>
      {comFiltro ? (
        <Button variant="secondary" size="sm" onClick={aoLimpar}>
          Limpar filtros
        </Button>
      ) : (
        <Button size="sm" onClick={aoAbrir} icon={<Plus className="h-4 w-4" aria-hidden />}>
          Abrir chamado
        </Button>
      )}
    </div>
  );
}

function EstadoDeErro({
  mensagem,
  aoTentarDeNovo,
}: {
  mensagem: string;
  aoTentarDeNovo: () => void;
}) {
  return (
    <div className="flex flex-col items-center gap-3 px-5 py-16 text-center">
      <p role="alert" className="text-sm text-destructive">
        {mensagem}
      </p>
      <Button
        variant="secondary"
        size="sm"
        onClick={aoTentarDeNovo}
        icon={<RotateCcw className="h-4 w-4" aria-hidden />}
      >
        Tentar de novo
      </Button>
    </div>
  );
}
